import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService, QuizQuestion, QuizSession } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';

/**
 * The quiz wizard. Renders one question at a time driven entirely by the
 * server's `next_question` payload — the frontend doesn't know the branching
 * logic. When the session transitions to `completed`, we navigate to /quiz/result.
 *
 * If there's no session token in storage (deep-link without going through
 * /quiz/start) we redirect back to /r to restart the funnel.
 */
@Component({
  selector: 'app-funnel-quiz-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-quiz-page.component.html',
  styleUrls: ['./funnel-quiz-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelQuizPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading  = signal(true);
  error    = signal<string | null>(null);
  question = signal<QuizQuestion | null>(null);
  busy     = signal(false);
  /** Bound to the textarea for free-text questions (e.g. the final "why" step). */
  textValue = signal('');

  constructor() {
    if (!this.funnel.getSessionToken()) {
      this.redirectToLanding();
      return;
    }
    this.fetchSession();
  }

  /** Submit the free-text answer (final "why" question). */
  submitText(): void {
    const q = this.question();
    if (!q) return;
    const text = this.textValue().trim();
    if (text.length < (q.min ?? 1)) return;
    this.sendAnswer(q.key, text);
  }

  selectAnswer(value: string): void {
    const q = this.question();
    if (!q || this.busy()) return;
    this.sendAnswer(q.key, value);
  }

  private sendAnswer(key: string, value: string): void {
    if (this.busy()) return;

    this.busy.set(true);
    this.error.set(null);

    this.funnel.answerQuiz(key, value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.busy.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'quiz_answered',
            name:     key
          });
          this.handleSession(session);
        },
        error: err => {
          this.busy.set(false);
          this.error.set(err?.error?.error || 'Could not save your answer. Please try again.');
        }
      });
  }

  private fetchSession(): void {
    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.loading.set(false);
          this.handleSession(session);
        },
        error: err => {
          this.loading.set(false);
          // 401 = expired/invalid session — start over.
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load the quiz.');
        }
      });
  }

  private handleSession(session: QuizSession): void {
    if (session.status === 'completed' || session.status === 'purchased') {
      if (session.recommended_product_code) {
        this.matomo.trackEvent({
          category: MATOMO_CATEGORY,
          action:   'quiz_completed',
          name:     session.recommended_product_code
        });
      }
      const lang = this.i18n.getLanguage() || 'en';
      this.router.navigate(['/', lang, 'quiz', 'result'], { replaceUrl: true });
      return;
    }
    this.textValue.set('');
    this.question.set(session.next_question);
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
